import { lazy, Suspense, type ComponentType } from "react";
import { BrowserRouter as Router, Navigate, Routes, Route } from "react-router-dom";
import BackToTop from "./components/BackToTop";
import ContactChatPopup from "./components/ContactChatPopup";
import { SmoothScroll } from "./components/SmoothScroll";
import { ContactChatProvider } from "./context/ContactChatContext";
import CatalogTranslationBridge from "./i18n/CatalogTranslationBridge";

// Route-level code splitting — mỗi page là 1 chunk riêng
const CHUNK_RELOAD_KEY = "bonvi:chunk-reload";

function lazyWithChunkRecovery<T extends ComponentType<object>>(importer: () => Promise<{ default: T }>) {
  return lazy(async () => {
    try {
      const module = await importer();
      sessionStorage.removeItem(CHUNK_RELOAD_KEY);
      return module;
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error);
      const isStaleChunk = /Failed to fetch dynamically imported module|Importing a module script failed|MIME type/i.test(message);
      if (isStaleChunk && sessionStorage.getItem(CHUNK_RELOAD_KEY) !== "1") {
        sessionStorage.setItem(CHUNK_RELOAD_KEY, "1");
        window.location.reload();
        return new Promise<never>(() => undefined);
      }
      throw error;
    }
  });
}

const HomePage         = lazyWithChunkRecovery(() => import("./pages/HomePage"));
const AboutPage        = lazyWithChunkRecovery(() => import("./pages/AboutPage"));
const ContactPage      = lazyWithChunkRecovery(() => import("./pages/ContactPage"));
const TermsPage        = lazyWithChunkRecovery(() => import("./pages/TermsPage"));
const PrivacyPolicyPage = lazyWithChunkRecovery(() => import("./pages/PrivacyPolicyPage"));
const PerformanceCreativePage = lazyWithChunkRecovery(() => import("./pages/PerformanceCreativePage"));
const ServiceDetailPage = lazyWithChunkRecovery(() => import("./pages/ServiceDetailPage"));
const OurWorkPage      = lazyWithChunkRecovery(() => import("./pages/OurWorkPage"));
const ProjectDetailPage = lazyWithChunkRecovery(() => import("./pages/ProjectDetailPage"));

export default function App() {
  return (
    <Router>
      <ContactChatProvider>
        <CatalogTranslationBridge />
        <SmoothScroll>
          <Suspense fallback={null}>
            <Routes>
              <Route path="/"                    element={<HomePage />} />
              <Route path="/about"               element={<AboutPage />} />
              <Route path="/services/performance-creative" element={<PerformanceCreativePage />} />
              <Route path="/services/:serviceSlug" element={<ServiceDetailPage />} />
              <Route path="/portfolio"           element={<OurWorkPage />} />
              <Route path="/projects/:projectSlug" element={<ProjectDetailPage />} />
              <Route path="/contact-us"          element={<ContactPage />} />
              <Route path="/terms-and-conditions" element={<TermsPage />} />
              <Route path="/privacy-policy"      element={<PrivacyPolicyPage />} />
              <Route path="*"                    element={<Navigate to="/" replace />} />
            </Routes>
          </Suspense>
          <BackToTop />
          <ContactChatPopup />
        </SmoothScroll>
      </ContactChatProvider>
    </Router>
  );
}
